import type { Progress, Milestone } from "../../shared/route/index.ts";
import type { ActivityType } from "../../shared/activities.ts";

export interface User {
  id: string;
  provider: "google";
  email: string | null;
  display_name: string;
  avatar_id: string;
  units: "mi" | "km";
  show_on_leaderboard: boolean;
  created_at: string;
}

export interface Fellowship {
  id: string;
  name: string;
  invite_code: string;
  created_by: string | null;
  created_at: string;
  member_count: number;
  role: "leader" | "member";
}

export interface Standing {
  user_id: string;
  display_name: string;
  avatar_id: string;
  total_meters: number;
  total_miles: number;
  activity_count: number;
  last_activity_on: string | null;
  segment_name: string;
  rank: number;
}
export type LeaderboardEntry = Standing;
export interface MemberStanding extends Standing { role: "leader" | "member"; joined_at: string }

export interface Companion {
  id: string;
  display_name: string;
  avatar_id: string;
  total_miles: number;
  fellowship: string;
}

export interface FellowshipDetail {
  fellowship: Fellowship;
  members: MemberStanding[];
  combined: { total_meters: number; total_miles: number; progress: Progress };
}

export interface ProgressSummary extends Progress {
  totalMeters: number;
  activityCount: number;
  firstActivityOn: string | null;
  lastActivityOn: string | null;
  unseenMilestones: number[];
}

export interface Activity {
  id: string;
  activity_type: ActivityType;
  distance_meters: number;
  duration_seconds: number | null;
  occurred_on: string;
  source: string;
  notes: string | null;
  created_at: string;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", accept: "application/json", ...(init.headers ?? {}) },
    credentials: "same-origin",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? res.statusText);
  return body as T;
}

export const api = {
  me: () => call<{ user: User; progress: ProgressSummary }>("/api/me"),
  updateMe: (patch: Partial<Pick<User, "display_name" | "avatar_id" | "units" | "show_on_leaderboard">>) =>
    call<{ user: User }>("/api/me", { method: "PATCH", body: JSON.stringify(patch) }),
  deleteMe: () => call<{ ok: true }>("/api/me", { method: "DELETE" }),
  loginGoogle: (credential: string) =>
    call<{ user: User }>("/api/auth/google", { method: "POST", body: JSON.stringify({ credential }) }),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  activities: () => call<{ activities: Activity[] }>("/api/activities"),
  addActivity: (a: { activity_type: ActivityType; distance_meters: number; occurred_on: string; duration_seconds?: number | null; notes?: string | null }) =>
    call<{ activity: Activity; crossed: Milestone[]; progress: ProgressSummary }>("/api/activities", { method: "POST", body: JSON.stringify(a) }),
  deleteActivity: (id: string) => call<{ ok: true; progress: ProgressSummary }>(`/api/activities/${id}`, { method: "DELETE" }),
  markMilestonesSeen: (miles: number[]) =>
    call<{ ok: true }>("/api/milestones/seen", { method: "POST", body: JSON.stringify({ miles }) }),
  fellowships: () => call<{ fellowships: Fellowship[] }>("/api/fellowships"),
  fellowship: (id: string) => call<FellowshipDetail>(`/api/fellowships/${id}`),
  createFellowship: (name: string) => call<{ fellowship: Fellowship }>("/api/fellowships", { method: "POST", body: JSON.stringify({ name }) }),
  joinFellowship: (code: string) => call<{ fellowship: Fellowship }>("/api/fellowships/join", { method: "POST", body: JSON.stringify({ code }) }),
  leaveFellowship: (id: string) => call<{ ok: true }>(`/api/fellowships/${id}/leave`, { method: "POST" }),
  companions: () => call<{ companions: Companion[] }>("/api/companions"),
  leaderboard: () => call<{ entries: LeaderboardEntry[]; me: { rank: number; of: number } | null }>("/api/leaderboard"),
};
